import { useEffect, useRef } from 'react';
import { BoxOverlay } from './BoxOverlay';
import type { HeroBox } from './realImages';

// The smallest crop the corners will make, as a fraction of the picture. A box with
// no area is not a crop, and a handle dragged past its opposite would invert the
// rectangle — this is what the corners stop at instead.
const MIN = 0.05;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

type Corner = 'tl' | 'tr' | 'bl' | 'br';

// Move one corner to (x, y) and return the rectangle that results.
//
// The shape cannot stop being a rectangle: the box IS [x0, y0, x1, y1], so a corner
// only ever moves two of the four numbers, and the two beside it follow. There is no
// state in which it could be anything else — which is why the corners are the whole
// interface and there is no "keep it rectangular" to enforce.
function moveCorner(box: HeroBox, corner: Corner, x: number, y: number): HeroBox {
  let [x0, y0, x1, y1] = box;
  const left = corner === 'tl' || corner === 'bl';
  const top = corner === 'tl' || corner === 'tr';
  // Each edge stops MIN short of its opposite rather than crossing it. Clamped, not
  // swapped: a corner dragged through the box would otherwise turn into the corner
  // opposite, and the picture would appear to jump under the cursor.
  if (left) x0 = Math.min(clamp01(x), x1 - MIN);
  else x1 = Math.max(clamp01(x), x0 + MIN);
  if (top) y0 = Math.min(clamp01(y), y1 - MIN);
  else y1 = Math.max(clamp01(y), y0 + MIN);
  return [x0, y0, x1, y1];
}

const HANDLES: { corner: Corner; at: string; cursor: string }[] = [
  { corner: 'tl', at: 'left-0 top-0 -translate-x-1/2 -translate-y-1/2', cursor: 'cursor-nwse-resize' },
  { corner: 'tr', at: 'right-0 top-0 translate-x-1/2 -translate-y-1/2', cursor: 'cursor-nesw-resize' },
  { corner: 'bl', at: 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2', cursor: 'cursor-nesw-resize' },
  { corner: 'br', at: 'right-0 bottom-0 translate-x-1/2 translate-y-1/2', cursor: 'cursor-nwse-resize' },
];

// A picture with a draggable crop rectangle on it: what will be cut, outlined, with
// everything outside it dimmed and a handle on each corner.
//
// Positioned in PERCENTAGES, which is the whole reason the box is normalized: the
// same numbers land correctly whatever size the picture renders at, and the crop the
// browser cuts is the crop that was on screen. The pointer works the other way — a
// drag is in pixels, and the container's own rect is what turns it back into a
// fraction, measured at every move rather than cached, because the page reflows.
export function CropEditor({
  src,
  box,
  onChange,
  alt,
  imgClassName = 'block max-h-80 rounded-lg',
}: {
  src: string;
  // Null means "no crop yet": the whole picture, and the corners start at its edges.
  box: HeroBox | null;
  onChange: (box: HeroBox) => void;
  alt?: string;
  // How the picture sizes — small in a panel, large in a zoom. The box is normalized, so
  // the corners land correctly whatever size it renders at.
  imgClassName?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const rect: HeroBox = box ?? [0, 0, 1, 1];

  // The drag reads the box from a ref, not from the closure it was created in: the
  // listeners are registered once per drag and would otherwise hold the box as it was
  // when the pointer went down, so every move would be computed from a stale
  // rectangle and the other corners would spring back.
  const boxRef = useRef(rect);
  boxRef.current = rect;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Registered on the WINDOW rather than the handle: a pointer moving faster than
  // React re-renders leaves the element behind, and a drag that stops when the cursor
  // outruns a 12px square is a drag that fights you at exactly the speed people
  // actually drag.
  const dragRef = useRef<Corner | null>(null);
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const corner = dragRef.current;
      const wrap = wrapRef.current;
      if (!corner || !wrap) return;
      e.preventDefault();
      const r = wrap.getBoundingClientRect();
      if (!r.width || !r.height) return;
      onChangeRef.current(
        moveCorner(boxRef.current, corner, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height),
      );
    };
    const up = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, []);

  const [x0, y0, x1, y1] = rect;
  return (
    <div ref={wrapRef} className="relative inline-block touch-none select-none">
      <img src={src} alt={alt} draggable={false} className={imgClassName} />

      <BoxOverlay box={rect} />

      {/* The handles sit OUTSIDE the clip: they straddle the rectangle's corners, so
          half of each would be cut away at the picture's edge — which is exactly where
          a crop of the whole picture starts, and where they are needed most. */}
      <div
        style={{
          left: `${x0 * 100}%`,
          top: `${y0 * 100}%`,
          width: `${(x1 - x0) * 100}%`,
          height: `${(y1 - y0) * 100}%`,
        }}
        className="pointer-events-none absolute"
      >
        {HANDLES.map((h) => (
          <span
            key={h.corner}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragRef.current = h.corner;
            }}
            className={`pointer-events-auto absolute h-3.5 w-3.5 rounded-sm border-2 border-white bg-blue-500 shadow ${h.at} ${h.cursor}`}
          />
        ))}
      </div>
    </div>
  );
}
